import React, { FunctionComponent, useMemo } from 'react';
import { compose } from 'recompose';
import { useTranslation, withTranslation, WithTranslation } from 'react-i18next';

// components
import AlertMessage from 'components/utils/AlertMessage';
import InteractiveTable from 'components/utils/InteractiveTable';

// constants
import { defaultPaginationOptions } from 'constants/pagination';
import { ApiResponse, LinkVlanExternalAttribute } from 'constants/api';

// utils
import { sizePerPageRenderer } from 'utils/commonUtils';
import { ApiDataBinding, withApiData } from 'react-api-data';
import { Card, CardBody, CardHeader } from 'reactstrap';
import { connect } from 'react-redux';
import { ReduxState } from 'createStore';
import { get } from 'lodash';
import RequestStatusRenderer from 'components/utils/RequestStatusRenderer';
import memoizeOne from 'memoize-one';
import i18next from 'locales/i18next';
import { Roles } from 'constants/literals';
import { getUserRolesSelector } from 'redux/apiData/apiDataSelectors';
import LinkVlanExternalAttributesAddModal from 'components/modals/LinkVlanExternalAttributesAddModal';
import LinkVlanExternalAttributesEditModal from 'components/modals/LinkVlanExternalAttributesEditModal';
import LinkVlanExternalAttributeDeleteModal from 'components/modals/LinkVlanExternalAttributeDeleteModal';

interface InProps {
    linkVlanUuid: string;
}

interface EnhanceProps {
    linkVlanExternalAttributesRequest: ApiDataBinding<ApiResponse<{external_attributes: LinkVlanExternalAttribute[]}>>;
    userRoles: Roles[];
}

type LinkVlanExternalAttributesProps = InProps & EnhanceProps & WithTranslation;

interface TableData {
    uuid: string;
    attribute_type: string;
    name: string;
    value: string;
    description: string;
}

interface LinkVlanExternalAttributesTableProps {
    linkVlanExtAttributes: LinkVlanExternalAttribute[];
    linkVlanUuid: string;
    userRoles: Roles[];
}
function buildTableData(extrenalAttributes: LinkVlanExternalAttribute[]) {
    return extrenalAttributes.map((extrenalAttribute) => {
        return {
            uuid: extrenalAttribute.uuid,
            attribute_type: extrenalAttribute.attribute_type,
            name: extrenalAttribute.name,
            value: extrenalAttribute.value,
            description: extrenalAttribute.description,
        };
    });
}
const getLinkVlanExternalAttributesTableColumns = memoizeOne((userRoles: Roles[], LinkVlanUuid: string): any => {
    return [
        {
            dataField: 'attribute_type',
            text: i18next.t('linkVlanExternalAttributes.attributeType'),
            sort: true,
        },
        {
            dataField: 'name',
            text: i18next.t('linkVlanExternalAttributes.attributeName'),
            sort: true,
        },
        {
            dataField: 'value',
            text: i18next.t('linkVlanExternalAttributes.attributeValue'),
            sort: true
        },
        {
            dataField: 'description',
            text: i18next.t('linkVlanExternalAttributes.attributeDescription'),
            sort: true,
            hidden: false
        },
        {
            dataField: 'uuid',
            hidden: true,
        },
        {
            dataField: 'edit',
            isDummyField: true,
            text: i18next.t('common.update'),
            formatter: (_: any, row: LinkVlanExternalAttribute) => {
                return (
                    <LinkVlanExternalAttributesEditModal
                        linkVlanExternalAttribute={row}
                        linkVlanUuid={LinkVlanUuid}
                    />
                );
            }
        },
        {
            dataField: 'remove',
            isDummyField: true,
            text: i18next.t('common.remove'),
            formatter: (_: any, row: LinkVlanExternalAttribute) => {
                return (
                    <LinkVlanExternalAttributeDeleteModal
                        linkVlanExternalAttributeUuid={row.uuid}
                        linkVlanUuid={LinkVlanUuid}
                    />
                );

            }
        },
    ];
});

function LinkVlanExternalAttributesTable(props: LinkVlanExternalAttributesTableProps) {
    const { linkVlanExtAttributes, linkVlanUuid, userRoles } = props;
    const { t } = useTranslation();
    const tableData = useMemo(() => buildTableData(linkVlanExtAttributes), [linkVlanExtAttributes]);

    return linkVlanExtAttributes.length !== 0 ? (
            <InteractiveTable
                data-test="linkVlanExternalAttributes-table"
                classes="table-responsive-lg"
                keyField="uuid"
                columns={getLinkVlanExternalAttributesTableColumns(userRoles, linkVlanUuid)}
                data={tableData}
                defaultSorted={[
                    {
                        dataField: 'attribute_type',
                        order: 'asc',
                    },
                ]}
                paginationOptions={{
                    ...defaultPaginationOptions,
                    sizePerPageRenderer: sizePerPageRenderer('up'),
                }}
            />
    ) : (
        <AlertMessage data-test="no-linkVlanExtAttributes-message" message={t('common.noData')} />
    );
}

const enhance = compose<LinkVlanExternalAttributesProps, InProps>(
    connect(
        (state: ReduxState) => ({
            userRoles: getUserRolesSelector(state),
        })
    ),
    withApiData({ linkVlanExternalAttributesRequest: 'getLinkVlanExternalAttributes' }, (ownProps: LinkVlanExternalAttributesProps) => ({
        linkVlanExternalAttributesRequest: { linkVlanUuid: ownProps.linkVlanUuid },
    })),
    withTranslation()
);

export const LinkVlanExternalAttributes: FunctionComponent<LinkVlanExternalAttributesProps> = ({
    linkVlanExternalAttributesRequest,
    linkVlanUuid,
    userRoles,
    t,
}) => {
   
    return (
        <Card className="card-accent-primary">
            <CardHeader>
                <strong>{t('linkVlanExternalAttributes.headerTitle')}</strong>
                <LinkVlanExternalAttributesAddModal
                        data-test="link_vlan-external_attributes-add"
                        linkVlanUuid={linkVlanUuid}
                />
            </CardHeader>
            <CardBody>
                <RequestStatusRenderer
                    request={linkVlanExternalAttributesRequest}
                    failedMessage={t('notification.unexpectedError')}
                    success={() => (
                        <LinkVlanExternalAttributesTable
                            linkVlanUuid={linkVlanUuid}
                            linkVlanExtAttributes={get(
                                linkVlanExternalAttributesRequest,
                                'data.data.external_attributes',
                                []
                            )}
                            userRoles={userRoles}
                        />
                    )}
                />
            </CardBody>
        </Card>
    );
};
export default enhance(LinkVlanExternalAttributes);
